// src/pages/PayPage.tsx
// v3.19 paywall surface — Starter / Pro / Ultimate tiers + refill for paying users.
// India-only pricing in phase 1 (USD/GCC unchanged).

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { paymentApi, authApi } from '../services/api';

type TierCode = 'pro' | 'ultimate' | 'refill';

interface TierTile {
  code: TierCode;
  name: string;
  priceLabel: string;
  cadence: string;
  credits: string;
  description: string;
  highlight?: boolean;
  cta: string;
}

// v3.19.7: Starter is the free 6-day trial — NOT purchasable. Pro is the lowest paid tier.
// Trial users come here via PaywallGuard or 402-event; they pay Pro or Ultimate to continue.
const TIERS: TierTile[] = [
  {
    code: 'pro',
    name: 'Pro',
    priceLabel: '₹249',
    cadence: 'per month',
    credits: '400 credits / mo',
    description: 'Daily learners. Unlimited Bookmate tutorials with download + share, plus full Workflow access.',
    cta: 'Go Pro',
  },
  {
    code: 'ultimate',
    name: 'Ultimate',
    priceLabel: '₹849',
    cadence: 'per month',
    credits: '1500 credits / mo',
    description: 'Power users — capstone projects, heavy research, larger codebases. Everything in Pro × 3.5.',
    highlight: true,  // v3.19.7.6: Most Popular badge — Francis's product call
    cta: 'Go Ultimate',
  },
];

// Stash purchase details for /pay/thanks (which fires the Google Ads purchase
// conversion), then hard-redirect so AuthContext re-inits from the
// freshly-written localStorage user — same reload semantics as the old
// direct-to-/codemate redirect.
const redirectToThanks = (info: { tier: string; amount?: number; paymentId?: string }) => {
  try { sessionStorage.setItem('cubemate_purchase', JSON.stringify(info)); } catch { /* best-effort */ }
  window.location.href = '/pay/thanks';
};

const PayPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [processing, setProcessing] = useState<TierCode | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  // Returning paying users can buy refill packs; brand-new users can't.
  // Lifetime-deal (ltd_*) users are eligible — refills are their only paid upsell.
  const refillEligible = user?.subscription_tier === 'pro' || user?.subscription_tier === 'ultimate'
    || (user?.subscription_tier || '').startsWith('ltd_');

  // Preload the Razorpay checkout script so the popup opens instantly.
  useEffect(() => {
    if ((window as any).Razorpay) return;
    const existing = document.querySelector('script[src*="checkout.razorpay.com"]');
    if (existing) return;
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  const openRazorpay = (order: any, tierCode: TierCode) => {
    const isSubscription = !!order.razorpay_subscription_id;
    const tierLabel = TIERS.find(t => t.code === tierCode)?.name || tierCode;
    const refillLabel = tierCode === 'refill' ? 'Refill — +100 credits' : `${tierLabel} plan`;

    const options: any = {
      key: order.razorpay_key_id,
      name: 'Cubemate',
      image: `${window.location.origin}/cubemate-logo.png`,
      description: isSubscription
        ? `${tierLabel} — ₹${order.amount}/month`
        : refillLabel,
      // v3.19.1: pass the actual customer so the Razorpay dashboard shows
      // the paying user (was defaulting to the merchant owner's email).
      prefill: {
        email: user?.email || '',
        name: user?.username || user?.email?.split('@')[0] || 'Cubemate User',
      },
      notes: {
        user_id: String(user?.id ?? ''),
        tier_code: tierCode,
        order_id: order.order_id || '',
      },
      handler: async (resp: any) => {
        try {
          setStatusMsg('Verifying payment…');
          const result = await paymentApi.verifyOrder(
            order.order_id,
            resp.razorpay_payment_id || '',
            resp.razorpay_subscription_id || '',
          );
          if (result.status === 'completed' || result.status === 'success') {
            // Refill doesn't change tier — already has access, redirect immediately.
            // v3.19.7.2: refresh cached user too so the new credit balance shows in
            // the sidebar / chat header on next render.
            if (tierCode === 'refill') {
              setStatusMsg('Refill credited. Taking you to your workspace…');
              try {
                const me = await authApi.validateToken();
                if (me) {
                  localStorage.setItem('user', JSON.stringify(me));
                  window.dispatchEvent(new CustomEvent('auth-token-saved', { detail: { user: me } }));
                }
              } catch { /* best-effort */ }
              setTimeout(() => redirectToThanks({
                tier: 'refill',
                amount: order.amount,
                paymentId: resp.razorpay_payment_id || '',
              }), 500);
              return;
            }
            // v3.19.1: poll /me until the new tier is actually live in DB.
            // Survives webhook lag, Razorpay async mandate, and AuthContext cache.
            // Without this, PaywallGuard can bounce the user back to /pay.
            //
            // v3.19.7.2: also write the fresh user back to localStorage AND dispatch
            // an auth-token-saved event so AuthContext updates BEFORE the redirect.
            // AuthContext.tsx:108-118 restores from localStorage on init without
            // re-fetching — without this write, Sidebar shows stale tier/credits
            // and Workflow stays locked until the user signs out + back in.
            setStatusMsg('Activating your plan…');
            const POLL_MS = 800;
            const TIMEOUT_MS = 10000;
            const start = Date.now();
            while (Date.now() - start < TIMEOUT_MS) {
              try {
                const me = await authApi.validateToken();
                if (me?.subscription_tier && me.subscription_tier !== 'basic') {
                  // Push the fresh tier into AuthContext's cache before navigating.
                  localStorage.setItem('user', JSON.stringify(me));
                  window.dispatchEvent(new CustomEvent('auth-token-saved', { detail: { user: me } }));
                  redirectToThanks({
                    tier: tierCode,
                    amount: order.amount,
                    paymentId: resp.razorpay_payment_id || '',
                  });
                  return;
                }
              } catch { /* transient — keep polling */ }
              await new Promise(r => setTimeout(r, POLL_MS));
            }
            // Timed out — redirect anyway; webhook will activate soon. Also do a
            // best-effort fresh fetch + cache write so the post-redirect Sidebar
            // reflects whatever state IS in the DB right now.
            try {
              const me = await authApi.validateToken();
              if (me) {
                localStorage.setItem('user', JSON.stringify(me));
                window.dispatchEvent(new CustomEvent('auth-token-saved', { detail: { user: me } }));
              }
            } catch { /* best-effort only */ }
            setStatusMsg('Almost there — taking you to your workspace.');
            redirectToThanks({
              tier: tierCode,
              amount: order.amount,
              paymentId: resp.razorpay_payment_id || '',
            });
          } else {
            setStatusMsg(null);
            setErrorMsg(`Payment is ${result.status}. We'll activate your plan as soon as Razorpay confirms.`);
            setProcessing(null);
          }
        } catch (err: any) {
          setStatusMsg(null);
          setErrorMsg(err?.message || 'Could not verify the payment. If money was deducted, it will be refunded.');
          setProcessing(null);
        }
      },
      modal: {
        // v3.19.7.6: User may close the popup AFTER the payment succeeded server-side
        // but BEFORE Razorpay's frontend confirms (common with UPI / slow bank
        // verification — popup hangs 20-60s on "processing", user gets impatient
        // and closes). In that case the `handler` callback never fires and the
        // user would see Starter in the sidebar despite having paid.
        // Before clearing state, poll /me ONCE — if the tier flipped, the webhook
        // already activated them; treat as a successful payment.
        ondismiss: async () => {
          try {
            const me = await authApi.validateToken();
            const tier = me?.subscription_tier;
            if (tier && tier !== 'basic' && tier !== 'starter') {
              // Payment actually succeeded despite dismissed popup — refresh + redirect
              localStorage.setItem('user', JSON.stringify(me));
              window.dispatchEvent(new CustomEvent('auth-token-saved', { detail: { user: me } }));
              setStatusMsg('Payment received — taking you to your workspace.');
              // No razorpay_payment_id here — popup was dismissed before the
              // handler fired; webhook confirmed the payment server-side.
              redirectToThanks({ tier: tierCode, amount: order.amount });
              return;
            }
          } catch { /* fall through to normal dismiss */ }
          setProcessing(null);
          setStatusMsg(null);
        },
      },
      theme: { color: '#7c3aed' },
    };

    if (isSubscription) {
      options.subscription_id = order.razorpay_subscription_id;
      options.redirect = false;
    } else if (order.razorpay_order_id) {
      options.order_id = order.razorpay_order_id;
      options.amount = order.amount * 100;
      options.currency = order.currency;
    }

    const launch = () => {
      const rzp = new (window as any).Razorpay(options);
      rzp.open();
    };

    if ((window as any).Razorpay) {
      launch();
    } else {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = launch;
      script.onerror = () => {
        setErrorMsg('Could not load the payment gateway. Try again in a moment.');
        setProcessing(null);
      };
      document.body.appendChild(script);
    }
  };

  const handlePick = async (code: TierCode) => {
    setErrorMsg(null);
    setStatusMsg(null);
    setProcessing(code);
    try {
      const order = await paymentApi.createOrder(code, 'razorpay', 'IN', 'monthly');
      if (order.payment_mode === 'popup' && order.razorpay_key_id) {
        openRazorpay(order, code);
      } else if (order.redirect_url) {
        window.location.href = order.redirect_url;
      } else {
        setErrorMsg('Unexpected payment response. Please try again.');
        setProcessing(null);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Could not start checkout.';
      setErrorMsg(msg);
      setProcessing(null);
    }
  };

  // v3.19.7: Tailored headline + subtext based on ?reason= query param.
  const reason = (typeof window !== 'undefined') ? new URLSearchParams(window.location.search).get('reason') : null;
  let pageHeadline = 'Choose a plan to get started';
  let pageSubtext = 'Pick a plan to unlock unlimited Bookmates, workflows, and sharing.';
  if (reason === 'workflow_requires_pro') {
    pageHeadline = 'Workflow needs Pro or Ultimate';
    pageSubtext = 'Workflows are part of Cubemate Pro. Upgrade to unlock automated pipelines + downloadable outputs.';
  } else if (reason === 'trial_expired') {
    pageHeadline = 'Your free trial ended';
    pageSubtext = 'Pick Pro or Ultimate to keep using Cubemate. Your chats and Bookmates stay saved either way.';
  } else if (reason === 'bookmate_cap') {
    pageHeadline = "You've used your 2 Starter Bookmates";
    pageSubtext = 'Upgrade to Pro for unlimited Bookmates — plus PDF download and public sharing.';
  } else if (reason === 'bookmate_pro_feature') {
    pageHeadline = 'PDF download and sharing are Pro features';
    pageSubtext = 'Upgrade to Pro to download Bookmates as PDFs and share them publicly.';
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 px-4 py-12">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">{pageHeadline}</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-3 max-w-xl mx-auto">
            {user?.email && (
              <>Signed in as <span className="font-medium">{user.email}</span>. </>
            )}
            {pageSubtext}
          </p>
        </div>

        {errorMsg && (
          <div className="max-w-2xl mx-auto mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-800 dark:text-red-200">
            {errorMsg}
          </div>
        )}
        {statusMsg && (
          <div className="max-w-2xl mx-auto mb-6 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-sm text-blue-800 dark:text-blue-200">
            {statusMsg}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {TIERS.map(t => (
            <div
              key={t.code}
              className={
                'relative rounded-2xl border p-6 flex flex-col bg-white dark:bg-gray-900 ' +
                (t.highlight
                  ? 'border-purple-400 dark:border-purple-500 shadow-lg ring-1 ring-purple-200 dark:ring-purple-900'
                  : 'border-gray-200 dark:border-gray-800')
              }
            >
              {t.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-medium rounded-full bg-purple-600 text-white">
                  Most popular
                </span>
              )}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t.name}</h2>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-gray-900 dark:text-white">{t.priceLabel}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">/ {t.cadence}</span>
                </div>
                <p className="mt-1 text-sm text-purple-700 dark:text-purple-400 font-medium">{t.credits}</p>
                <p className="mt-4 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{t.description}</p>
              </div>

              <button
                type="button"
                disabled={!!processing}
                onClick={() => handlePick(t.code)}
                className={
                  'mt-6 w-full py-2.5 rounded-lg font-medium text-sm transition ' +
                  (t.highlight
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900') +
                  (processing ? ' opacity-60 cursor-not-allowed' : '')
                }
              >
                {processing === t.code ? 'Opening Razorpay…' : t.cta}
              </button>
            </div>
          ))}
        </div>

        {refillEligible && (
          <div className="max-w-2xl mx-auto mt-10 p-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Need more credits this month?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                ₹99 for +100 credits. Valid until your subscription renews.
              </p>
            </div>
            <button
              type="button"
              disabled={!!processing}
              onClick={() => handlePick('refill')}
              className="ml-4 px-4 py-2 rounded-lg font-medium text-sm bg-purple-600 hover:bg-purple-700 text-white transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {processing === 'refill' ? 'Opening…' : 'Add 100 credits'}
            </button>
          </div>
        )}

        {/* Deal-site (AppSumo etc.) buyers who wandered in the front door —
            /pay is the wall every non-subscribed user hits, so the redemption
            path must be visible here */}
        <p className="text-center mt-10 text-sm text-gray-600 dark:text-gray-300">
          Bought Cubemate on a deal site?{' '}
          <button
            onClick={() => navigate('/redeem')}
            className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 underline font-medium"
          >
            Redeem your code
          </button>
        </p>

        <div className="text-center mt-3">
          <button
            onClick={() => navigate(user ? '/codemate' : '/')}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline"
          >
            Back to {user ? 'app' : 'home'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayPage;
